import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ArrowLeft, Clipboard, RefreshCw, XCircle} from "lucide-react";
import QRCode from "react-qr-code";
import {Link, useParams} from "react-router-dom";
import {getAuthToken} from "../../shared/api/client";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {DetailGrid} from "../../shared/components/DetailGrid";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime} from "../../shared/utils/format";
import {
    cancelSplitBill,
    getSplitBill,
    getSplitBillStatus,
    splitBillKeys,
} from "./api";
import {
    canCancelSplit,
    getSplitGroupStatusPresentation,
    getSplitPartStatusPresentation,
    shouldPollSplit,
} from "./statusMachine";
import {useSplitCountryScope} from "./useSplitCountryScope";
import {useTranslation} from "../../shared/i18n";
import "./splitBills.css";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const splitPollInterval = (snapshot, hidden) =>
    !hidden && shouldPollSplit(snapshot?.group) ? 5000 : false;

export const freshestSplitSnapshot = ({
    cancellation,
    detail,
    detailUpdatedAt = 0,
    status,
    statusUpdatedAt = 0,
}) => {
    if (cancellation) return cancellation;
    if (status && statusUpdatedAt > detailUpdatedAt) return status;
    return detail || status || null;
};

export const SplitBillDetailPage = () => {
    const {t} = useTranslation();
    const {groupId} = useParams();
    const validGroupId = UUID_PATTERN.test(groupId || "") ? groupId : null;
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const canView = capabilities.hasAction("split_bills.view");
    const {countryScope, activeCountryScopeRef, scopeBlock} = useSplitCountryScope(capabilities);
    const [pageHidden, setPageHidden] = useState(() => document.hidden);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [commandError, setCommandError] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);
    const [copyMessage, setCopyMessage] = useState("");
    const detailKey = splitBillKeys.detail(countryScope, validGroupId);
    const statusKey = splitBillKeys.status(countryScope, validGroupId);

    useEffect(() => {
        const handleVisibility = () => setPageHidden(document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    useEffect(() => {
        setCancelOpen(false);
        setCommandError(null);
        setMutationResult(null);
        setCopyMessage("");
    }, [countryScope]);

    const detailQuery = useQuery({
        queryKey: detailKey,
        queryFn: () => getSplitBill({
            countryScope,
            actorCountryId: capabilities.country?.id,
            groupId: validGroupId,
        }),
        enabled: canView && validGroupId !== null && countryScope !== null && scopeBlock === null,
        retry: false,
    });

    const statusQuery = useQuery({
        queryKey: statusKey,
        queryFn: () => getSplitBillStatus({
            countryScope,
            actorCountryId: capabilities.country?.id,
            groupId: validGroupId,
        }),
        enabled: Boolean(canView && detailQuery.data && shouldPollSplit(detailQuery.data.group) && scopeBlock === null),
        retry: false,
        refetchInterval: (query) =>
            splitPollInterval(query.state.data || detailQuery.data, pageHidden),
        refetchIntervalInBackground: false,
    });
    const cancelMutation = useMutation({
        mutationFn: ({requestScope, mutationGroupId}) =>
            cancelSplitBill({
                countryScope: requestScope,
                actorCountryId: capabilities.country?.id,
                groupId: mutationGroupId,
            }),
        onSuccess: (updated, variables) => {
            if (
                getAuthToken() !== variables.requestSession ||
                activeCountryScopeRef.current !== variables.requestScope
            ) return;
            queryClient.setQueryData(
                splitBillKeys.detail(variables.requestScope, variables.mutationGroupId),
                updated,
            );
            queryClient.setQueryData(
                splitBillKeys.status(variables.requestScope, variables.mutationGroupId),
                updated,
            );
            void queryClient.invalidateQueries({
                queryKey: splitBillKeys.all(variables.requestScope),
            });
            setCancelOpen(false);
            setCommandError(null);
            setMutationResult({kind: "success", message: t("money.splitBills.detail.cancelled")});
        },
        onError: async (error, variables) => {
            if (getAuthToken() !== variables.requestSession) return;
            const mapped = mapFinanceError(error);
            if (mapped.kind === "conflict") {
                if (activeCountryScopeRef.current === variables.requestScope) {
                    setCancelOpen(false);
                    setCommandError(null);
                    setMutationResult(mapped);
                }
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: splitBillKeys.detail(variables.requestScope, variables.mutationGroupId),
                    }),
                    queryClient.invalidateQueries({
                        queryKey: splitBillKeys.status(variables.requestScope, variables.mutationGroupId),
                    }),
                    queryClient.invalidateQueries({queryKey: splitBillKeys.all(variables.requestScope)}),
                ]);
                return;
            }
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setCommandError(mapped);
        },
    });
    const cancellationSnapshot =
        cancelMutation.isSuccess &&
        cancelMutation.variables?.requestScope === countryScope &&
        cancelMutation.variables?.mutationGroupId === validGroupId
            ? cancelMutation.data
            : null;
    const snapshot = freshestSplitSnapshot({
        cancellation: cancellationSnapshot,
        detail: detailQuery.data,
        detailUpdatedAt: detailQuery.dataUpdatedAt,
        status: statusQuery.data,
        statusUpdatedAt: statusQuery.dataUpdatedAt,
    });

    if (validGroupId === null) {
        return <DataState title={t("money.splitBills.detail.invalidTitle")} copy={t("money.splitBills.detail.invalidCopy")} />;
    }

    if (!canView) {
        return <DataState title={t("money.splitBills.detail.unavailableTitle")} copy={t("money.splitBills.detail.unavailableCopy")} />;
    }

    if (scopeBlock) {
        return (
            <div className="admin-page split-detail-page">
                <Link className="split-back-link" to="/finance/split-bills">
                    <ArrowLeft aria-hidden="true" size={16} /> {t("money.splitBills.detail.back")}
                </Link>
                <DataState title={scopeBlock.title} copy={scopeBlock.copy} />
            </div>
        );
    }

    if (detailQuery.isLoading) {
        return (
            <div className="admin-table-skeleton" aria-label={t("money.splitBills.detail.loading")}>
                <span /><span /><span />
            </div>
        );
    }

    if (detailQuery.isError || !snapshot) {
        return (
            <DataState
                title={t("money.splitBills.detail.loadError")}
                copy={mapFinanceError(detailQuery.error).message}
            />
        );
    }

    const groupStatus = getSplitGroupStatusPresentation(snapshot.group.status);
    const paidParts = snapshot.parts.filter(({status}) => status === "paid").length;
    const cancellable = canCancelSplit(snapshot.group, snapshot.parts, capabilities.hasAction);

    const copyLink = async (part) => {
        try {
            await navigator.clipboard.writeText(part.pay_url);
            setCopyMessage(t("money.splitBills.detail.copied", {number: part.part_number}));
        } catch (_error) {
            setCopyMessage(t("money.splitBills.detail.copyFailed"));
        }
    };

    return (
        <div className="admin-page split-detail-page">
            <Link className="split-back-link" to="/finance/split-bills">
                <ArrowLeft aria-hidden="true" size={16} /> {t("money.splitBills.detail.back")}
            </Link>
            <header className="admin-page-header split-detail-header">
                <div>
                    <p className="admin-eyebrow">{t("money.splitBills.detail.eyebrow")}</p>
                    <h1 className="admin-page-title">Split bill</h1>
                    <p className="admin-page-copy">#{snapshot.group.id}</p>
                </div>
                <StatusBadge tone={groupStatus.tone}>{t(`money.splitBills.statuses.${groupStatus.known ? snapshot.group.status : "unknown"}`)}</StatusBadge>
            </header>

            <MutationResultBanner result={mutationResult} />
            {statusQuery.isError ? (
                <p className="admin-form-error" role="alert">
                    {t("money.splitBills.detail.stale")}
                </p>
            ) : null}
            {copyMessage ? <p className="split-copy-status" role="status">{copyMessage}</p> : null}

            <section className="split-summary" aria-labelledby="split-summary-title">
                <div className="split-progress-copy">
                    <p className="admin-eyebrow">{t("money.splitBills.detail.progressEyebrow")}</p>
                    <h2 id="split-summary-title">{t("money.splitBills.paidOf", {paid: paidParts, total: snapshot.group.parts_count})}</h2>
                    <div className="split-progress-track" aria-label={t("money.splitBills.paidOf", {paid: paidParts, total: snapshot.group.parts_count})}>
                        <span style={{width: `${(paidParts / snapshot.group.parts_count) * 100}%`}} />
                    </div>
                </div>
                <DetailGrid items={[
                    {label: t("money.splitBills.detail.amount"), value: formatExactMoney(snapshot.group.original_amount)},
                    {label: t("money.splitBills.detail.branch"), value: snapshot.group.branch_name},
                    {label: t("money.splitBills.detail.staff"), value: snapshot.group.personal_name || t("money.splitBills.detail.noStaff")},
                    {label: t("money.splitBills.detail.parts"), value: String(snapshot.group.parts_count)},
                    {label: t("money.splitBills.detail.expires"), value: formatDateTime(snapshot.group.expires_at)},
                    {label: t("money.splitBills.detail.created"), value: formatDateTime(snapshot.group.created_at)},
                ]} />
                {cancellable ? (
                    <button
                        className="admin-button admin-button-danger"
                        type="button"
                        onClick={() => {
                            setCancelOpen(true);
                            setCommandError(null);
                            setMutationResult(null);
                        }}
                    >
                        <XCircle aria-hidden="true" size={16} /> {t("money.splitBills.detail.cancel")}
                    </button>
                ) : null}
            </section>

            <section className="split-parts" aria-labelledby="split-parts-title">
                <div className="split-section-heading">
                    <div>
                        <p className="admin-eyebrow">{t("money.splitBills.detail.linksEyebrow")}</p>
                        <h2 id="split-parts-title">{t("money.splitBills.detail.partsTitle")}</h2>
                    </div>
                    {statusQuery.isFetching ? (
                        <span className="split-refresh-status" role="status">
                            <RefreshCw aria-hidden="true" size={14} /> {t("money.splitBills.detail.refreshing")}
                        </span>
                    ) : null}
                </div>
                <div className="split-parts-grid">
                    {snapshot.parts.map((part) => {
                        const partStatus = getSplitPartStatusPresentation(part.status);
                        return (
                            <article className="split-part-card" key={part.id}>
                                <div className="split-part-header">
                                    <div>
                                        <p className="admin-eyebrow">{t("money.splitBills.detail.part", {number: part.part_number})}</p>
                                        <h3>{formatExactMoney(part.amount)}</h3>
                                    </div>
                                    <StatusBadge tone={partStatus.tone}>{t(`money.splitBills.partStatuses.${partStatus.known ? part.status : "unknown"}`)}</StatusBadge>
                                </div>
                                <div className="split-part-content">
                                    <div className="split-qr-frame" aria-label={t("money.splitBills.detail.qr", {number: part.part_number})}>
                                        <QRCode value={part.pay_url} size={116} />
                                    </div>
                                    <div className="split-part-links">
                                        <p><span>{t("money.splitBills.detail.payment")}</span><code>{part.pay_bill_id}</code></p>
                                        <p><span>Intent</span><code>{part.payment_intent_url}</code></p>
                                        <a href={part.pay_url} target="_blank" rel="noreferrer">{t("money.splitBills.detail.openPay")}</a>
                                        <button
                                            className="admin-button admin-button-secondary"
                                            type="button"
                                            aria-label={t("money.splitBills.detail.copyAria", {number: part.part_number})}
                                            title={t("money.splitBills.detail.copyTitle")}
                                            onClick={() => void copyLink(part)}
                                        >
                                            <Clipboard aria-hidden="true" size={15} /> {t("money.splitBills.detail.copy")}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <FinancialCommandDialog
                open={cancelOpen}
                dialogKey={validGroupId}
                title={t("money.splitBills.detail.cancel")}
                description={t("money.splitBills.detail.cancelDescription")}
                entity={t("money.splitBills.detail.groupEntity", {id: validGroupId})}
                details={[
                    {label: t("money.splitBills.detail.amount"), value: formatExactMoney(snapshot.group.original_amount)},
                    {label: t("money.splitBills.detail.parts"), value: String(snapshot.group.parts_count)},
                    {label: t("money.splitBills.detail.status"), value: t(`money.splitBills.statuses.${groupStatus.known ? snapshot.group.status : "unknown"}`)},
                ]}
                pending={cancelMutation.isPending}
                error={commandError}
                confirmLabel={t("money.splitBills.detail.cancel")}
                onCancel={() => {
                    if (cancelMutation.isPending) return;
                    setCancelOpen(false);
                    setCommandError(null);
                }}
                onConfirm={() => cancelMutation.mutate({
                    requestScope: countryScope,
                    requestSession: getAuthToken(),
                    mutationGroupId: validGroupId,
                })}
            />
        </div>
    );
};
